import browser from "webextension-polyfill";
import * as ewe from "@eyeo/webext-ad-filtering-solution";
import { CommandEventType, CommandName, CommandVersion, DeleteEventType, maximumProcessableCommands, } from "./command-library.types";
import * as commandStorage from "./command-storage";
import { getStartInfo, setStartInfo } from "./start-info";
import { isDeleteBehavior, setDeleteCommandHandler } from "./delete-commands";
import { recordEvent, recordGenericEvent } from "./event-recording";
import { checkLanguage } from "./language-check";
import { isValidDate } from "./param-validator";
const knownCommandsList = Object.values(CommandName);
const actorByCommandName = new Map();
const unexecutableCommands = new Map();
const serverLogger = new ewe.telemetry.ServerLogger("in_product_messaging");
function isCommand(candidate) {
    return (typeof candidate === "object" &&
        candidate !== null &&
        "version" in candidate &&
        "ipm_id" in candidate &&
        "command_name" in candidate &&
        "expiry" in candidate);
}
export function isCommandMap(candidate) {
    return (typeof candidate === "object" &&
        candidate !== null &&
        Object.keys(candidate).every((key) => typeof key === "string") &&
        Object.values(candidate).every(isCommand));
}
export function setCommandActor(commandName, actor) {
    actorByCommandName.set(commandName, actor);
    retryExecuteCommands(commandName);
}
export function dismissCommand(ipmId) {
    const { getDismissedIpmIds, setDismissedIpmIds } = getStartInfo();
    const dismissedIpmIds = getDismissedIpmIds();
    dismissedIpmIds.push(ipmId);
    setDismissedIpmIds(dismissedIpmIds);
    commandStorage.remove(ipmId);
}
export function getBehavior(ipmId) {
    const command = getCommand(ipmId);
    if (!command) {
        return null;
    }
    const actor = actorByCommandName.get(command.command_name);
    if (!actor) {
        return null;
    }
    return actor.getBehavior(command);
}
export function getCommand(ipmId) {
    return commandStorage.get(ipmId);
}
export function getContent(ipmId) {
    const command = getCommand(ipmId);
    if (!command) {
        return null;
    }
    const actor = actorByCommandName.get(command.command_name);
    if (!actor) {
        return null;
    }
    return actor.getContent(command);
}
export function isCommandExpired(command) {
    if (!isValidDate(command.expiry)) {
        return true;
    }
    const expiryDateOnly = new Date(command.expiry);
    const nowDateTime = new Date();
    if (nowDateTime >= expiryDateOnly) {
        return true;
    }
    return false;
}
function retryExecuteCommands(commandName) {
    for (const [command, isInitialization] of unexecutableCommands) {
        if (command.command_name !== commandName) {
            continue;
        }
        unexecutableCommands.delete(command);
        void executeIPMCommands([command], isInitialization);
    }
}
export async function executeIPMCommands(commands, isInitialization = false) {
    const { logger } = getStartInfo();
    const actorByExecutableCommand = new Map();
    if (commands.length > maximumProcessableCommands) {
        logger.reportAndLogError(new Error("[ipm]: Too many commands received."));
        recordGenericEvent("too_many_commands");
        return;
    }
    for (const command of commands) {
        if (!isCommand(command)) {
            logger.reportAndLogError(new Error("[ipm]: Invalid command received."));
            continue;
        }
        if (!knownCommandsList.includes(command.command_name)) {
            logger.reportAndLogError(new Error(`[ipm]: Unknown command name received: ${command.command_name}`));
            continue;
        }
        if (command.version !== CommandVersion[command.command_name]) {
            logger.reportAndLogError(new Error(`[ipm]: Command version mismatch for command "${command.command_name}". Requested version was ${command.version}, version present is ${CommandVersion[command.command_name]}`));
            if (isInitialization) {
                dismissCommand(command.ipm_id);
            }
            continue;
        }
        if (isCommandExpired(command)) {
            logger.reportAndLogError(new Error("[ipm]: Command has expired."));
            recordEvent(command.ipm_id, command.command_name, CommandEventType.expired);
            if (isInitialization) {
                dismissCommand(command.ipm_id);
            }
            continue;
        }
        const actor = actorByCommandName.get(command.command_name);
        if (!actor) {
            logger.debug("[ipm]: No actor found:", command.command_name);
            unexecutableCommands.set(command, isInitialization);
            continue;
        }
        if (!actor.isValidCommand(command)) {
            logger.reportAndLogError(new Error("[ipm]: Invalid parameters received."));
            continue;
        }
        if (!("attributes" in command)) {
            command.attributes = {
                received: Date.now(),
                language: browser.i18n.getUILanguage(),
            };
        }
        actorByExecutableCommand.set(command, actor);
    }
    const executableCommands = Array.from(actorByExecutableCommand.keys());
    if (!isInitialization && executableCommands.length > 0) {
        commandStorage.add(executableCommands);
    }
    const commandHandlingPromises = Array.from(actorByExecutableCommand).map(async ([executableCommand, actor]) => {
        serverLogger.info("command_received", executableCommand.ipm_id);
        await actor.handleCommand(executableCommand.ipm_id, isInitialization);
    });
    void Promise.all(commandHandlingPromises).then(() => {
        knownCommandsList
            .map((name) => actorByCommandName.get(name))
            .forEach((actor) => actor === null || actor === void 0 ? void 0 : actor.onCommandsProcessed());
    });
}
function registerDeleteEvent(ipmId, name) {
    recordEvent(ipmId, CommandName.deleteCommands, name);
}
async function handleDeleteCommand(ipmId) {
    const { logger } = getStartInfo();
    const command = getCommand(ipmId);
    if (!command) {
        return;
    }
    const behavior = getBehavior(ipmId);
    if (!isDeleteBehavior(behavior)) {
        logger.reportAndLogError(new Error("[delete-commands]: Invalid command behavior."));
        return;
    }
    void checkLanguage(ipmId);
    if (isCommandExpired(command)) {
        logger.reportAndLogError(new Error("[delete-commands]: Command has expired."));
        registerDeleteEvent(ipmId, CommandEventType.expired);
        dismissCommand(ipmId);
        return;
    }
    const { commandIds } = behavior;
    let success = true;
    for (const commandId of commandIds) {
        try {
            dismissCommand(commandId);
            if (getCommand(commandId) !== null) {
                throw new Error("Command was not successfully deleted.");
            }
        }
        catch (error) {
            success = false;
            logger.reportAndLogError(new Error(`[delete-commands]: Error trying to delete command with ID ${commandId}`));
        }
    }
    registerDeleteEvent(ipmId, success ? DeleteEventType.sucess : DeleteEventType.error);
    dismissCommand(ipmId);
}
export async function clearCommands() {
    for (const actor of actorByCommandName.values()) {
        await actor.clearCommands();
    }
}
async function getRemoteCommands() {
    const { logger } = getStartInfo();
    const remoteCommandCandidates = await ewe.remoteConfig.getIpmCommands();
    if (remoteCommandCandidates === null) {
        return null;
    }
    const remoteCommands = [];
    for (const candidate of remoteCommandCandidates) {
        if (!isCommand(candidate)) {
            logger.reportAndLogError(new Error("[ipm]: Invalid command received."));
            continue;
        }
        remoteCommands.push(candidate);
    }
    return remoteCommands;
}
function clearDismissedIpmIds() {
    const { setDismissedIpmIds } = getStartInfo();
    setDismissedIpmIds([]);
}
function handleTestingGetCommandsMessage() {
    return commandStorage.getAll();
}
async function synchronizeRemoteCommands() {
    const { getDismissedIpmIds, logger, setDismissedIpmIds } = getStartInfo();
    const remoteCommands = await getRemoteCommands();
    if (remoteCommands === null) {
        logger.reportAndLogError(new Error("[ipm]: No remote configuration."));
        return;
    }
    const dismissedIpmIds = getDismissedIpmIds();
    const commands = remoteCommands.filter((command) => {
        return !dismissedIpmIds.includes(command.ipm_id);
    });
    const newDismissedIpmIds = dismissedIpmIds.filter((ipmId) => {
        return remoteCommands.some((command) => command.ipm_id === ipmId);
    });
    setDismissedIpmIds(newDismissedIpmIds);
    const commandMap = Object.fromEntries(commands.map((command) => [command.ipm_id, command]));
    commandStorage.replaceAll(commandMap);
    await executeIPMCommands(commands, true);
}
export async function start(startInfo) {
    setStartInfo(startInfo);
    setDeleteCommandHandler(handleDeleteCommand);
    await synchronizeRemoteCommands();
    ewe.remoteConfig.onChanged.addListener(synchronizeRemoteCommands);
    startInfo.addMessageListener("ipm:testing.clearDismissedIpmIds", clearDismissedIpmIds);
    startInfo.addMessageListener("ipm:testing.getCommands", handleTestingGetCommandsMessage);
}
export async function stop() {
    const { removeMessageListener } = getStartInfo();
    await clearCommands();
    ewe.remoteConfig.onChanged.removeListener(synchronizeRemoteCommands);
    removeMessageListener("ipm:testing.clearDismissedIpmIds", clearDismissedIpmIds);
    removeMessageListener("ipm:testing.getCommands", handleTestingGetCommandsMessage);
}
